import axios from 'axios'
import queryString from 'query-string'
import { ExpenseReportResponse } from 'src/models/expenseReport.model'
import { UserListResponse } from 'src/models/user.models'
import {
  ExpenseReportsListParams,
  ExpenseReportsSuccessPayload,
} from 'src/queries/expenseReports/expenseReports.models'
import { getResponseData } from 'src/queries/network.utils'
import { getRequestConfig, RequestConfig } from 'src/queries/requestConfig'

/**
 * RequestConfig.Default mirrors the legacy useExtendInfiniteQuery wrappers,
 * which resolved useRequestConfig(RequestConfig.Default) ->
 * selectAuthedJsonApiRequestConfig for every OOPE list/count call.
 */
const listUrl = (
  path: string,
  { filters, page, perPage }: ExpenseReportsListParams,
): string => `${path}?${queryString.stringify({ ...filters, page, perPage })}`

export const getSubmitterExpenseReports = async (
  params: ExpenseReportsListParams,
): Promise<ExpenseReportsSuccessPayload> => {
  return getResponseData(
    axios.get<ExpenseReportsSuccessPayload>(
      listUrl('/expensereports/submitter', params),
      getRequestConfig(RequestConfig.Default),
    ),
  )
}

export const getApproverExpenseReports = async (
  params: ExpenseReportsListParams,
): Promise<ExpenseReportsSuccessPayload> => {
  return getResponseData(
    axios.get<ExpenseReportsSuccessPayload>(
      listUrl('/expensereports/approver', params),
      getRequestConfig(RequestConfig.Default),
    ),
  )
}

export const getDirectReportsExpenseReports = async (
  params: ExpenseReportsListParams,
): Promise<ExpenseReportsSuccessPayload> => {
  return getResponseData(
    axios.get<ExpenseReportsSuccessPayload>(
      listUrl('/expensereports/directreports', params),
      getRequestConfig(RequestConfig.Default),
    ),
  )
}

/**
 * OOPE report detail read (GET /expensereports/:id). Replaces the legacy
 * `getExpenseReport` redux thunk and the `useQueryOOPEReportDetail`
 * (useExtendQuery) wrapper, which both hit this endpoint with
 * selectAuthedJsonApiRequestConfig === getRequestConfig(RequestConfig.Default).
 */
export const getExpenseReportDetail = async (
  reportId: string,
): Promise<ExpenseReportResponse> => {
  return getResponseData(
    axios.get<ExpenseReportResponse>(
      `/expensereports/${reportId}`,
      getRequestConfig(RequestConfig.Default),
    ),
  )
}

/**
 * Submitter-user list for the reimbursements filter dropdown
 * (GET /expensereports/submitterusers). The endpoint returns the full
 * UserListResponse[] in one shot (not paginated); the legacy redux flow's
 * "infinite scroll" was a no-op after the first fetch.
 */
export const getSubmitterUsers = async (): Promise<UserListResponse[]> => {
  return getResponseData(
    axios.get<UserListResponse[]>(
      `/expensereports/submitterusers`,
      getRequestConfig(RequestConfig.Default),
    ),
  )
}
